import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageService from "../../../services/KangarooServices/PageService";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { formatDateTime, formatDateTimes } from "../../../Components/Common/FormatDate";
import { Calendar, Edit3, FileText,Globe ,Tag,User,Clock, } from "lucide-react";

const PageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [defaultData, setDefaultData] = useState();
  
  const fetchDetail = async () => {
    try {
      const response = await PageService.getDetail(id);
      setDefaultData(response);
    } catch (error) {
      console.log(error);
    }
  };
  
  useEffect(() => {
    fetchDetail();
  }, [id]);

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower === "published" || statusLower === "active") {
      return <Badge color="success-subtle" className="text-success border border-success-subtle px-3 py-2 rounded-pill fw-bold">Published</Badge>;
    }
    return <Badge color="warning-subtle" className="text-warning border border-warning-subtle px-3 py-2 rounded-pill fw-bold">Draft</Badge>;
  };
  
  const breadcrumbItems = [{ title: "Back to List", link: "/pages-list" }];
  
  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Details"
          pageTitle="Pages"
          breadcrumbItems={breadcrumbItems}
        />

        <Row className="justify-content-center">
          <Col lg={12}>
            {/* Header Card */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-4">
                <div className="d-sm-flex align-items-center justify-content-between pb-3 border-bottom border-light">
                  <div>
                    <h3 className="fw-bold mb-1 text-primary text-gradient">
                      {defaultData?.title || "Untitled Page"}
                    </h3>
                    <div className="d-flex align-items-center gap-3 text-muted small">
                      <span className="d-flex align-items-center gap-1">
                        <Calendar size={14} className="text-info" /> 
                        Last updated: {defaultData?.updatedAt 
                          ? formatDateTimes(defaultData?.updatedAt) 
                          : formatDateTime(defaultData?.createdAt)}
                      </span>
                      <span className="text-light">|</span>
                      {getStatusBadge(defaultData?.status)}
                    </div>
                    {defaultData?.subtitle && (
                      <p className="text-muted mb-0 mt-2">{defaultData?.subtitle}</p>
                    )}
                  </div>
                  <div className="mt-3 mt-sm-0">
                    <Button
                      color="primary"
                      className="btn-soft-primary d-flex align-items-center gap-2 shadow-sm px-4"
                      onClick={() => navigate(`/pages/update/${id}`)}
                    >
                      <Edit3 size={16} /> Update Page
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Row>
              {/* Main Content Column */}
              <Col lg={8}>
                {/* Page Content Card */}
                <Card className="border-0 border-top border-3 border-primary shadow-sm mb-4">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 fw-bold d-flex align-items-center gap-2 text-dark">
                      <FileText size={18} className="text-primary" />
                      Page Content
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    {defaultData?.content ? (
                      <div
                        className="text-secondary lh-lg"
                        dangerouslySetInnerHTML={{ __html: defaultData?.content }}
                      />
                    ) : (
                      <p className="text-muted fst-italic mb-0 bg-light p-3 rounded text-center">
                        No content available
                      </p>
                    )}
                  </CardBody>
                </Card>
              </Col>

              {/* Sidebar Column */}
              <Col lg={4}>
                {/* SEO Information Card */}
                <Card className="border-0 shadow-sm mb-4 border-top border-primary border-3">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                      <Globe size={18} className="text-primary" />
                      SEO Information
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold text-primary small text-uppercase mb-1">
                        Meta Title
                      </label>
                      <p className="mb-0 fw-medium text-dark border-start border-primary border-2 ps-2">
                        {defaultData?.metaTitle || (
                          <span className="text-muted fst-italic">Not set</span>
                        )}
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold text-muted small text-uppercase mb-1">
                        Meta Description
                      </label>
                      <p className="mb-0 text-muted small bg-light p-2 rounded">
                        {defaultData?.metaDescription || (
                          <span className="text-muted fst-italic">Not set</span>
                        )}
                      </p>
                    </div>

                    {/* <div className="border-top pt-3 mb-3">
                      <label className="form-label fw-bold text-muted small text-uppercase mb-1 d-flex align-items-center gap-1">
                        <Tag size={14} className="text-primary" />
                        Slug
                      </label>
                      <div className="p-2 bg-primary-subtle rounded">
                        <code className="text-primary fw-bold">
                          /{defaultData?.slug || "N/A"}
                        </code>
                      </div>
                    </div> */}

                    <div className="border-top pt-3">
                      <label className="form-label fw-bold text-muted small text-uppercase mb-1">
                        Current Status
                      </label>
                      <div>
                        {getStatusBadge(defaultData?.status)}
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Additional Information Card */}
                <Card className="border-0 shadow-sm mb-4">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                      <FileText size={18} className="text-primary" />
                      Page Information
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    <div className="mb-3">
                      <small className="text-muted d-block text-uppercase fw-bold mb-1" 
                        style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                        Created At
                      </small>
                      <div className="d-flex align-items-center gap-2 text-muted small">
                        <User size={14} className="text-primary" />
                        <span>
                          {defaultData?.createdAt 
                            ? formatDateTime(defaultData?.createdAt) 
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="border-top pt-3">
                      <small className="text-muted d-block text-uppercase fw-bold mb-1" 
                        style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                        Last Modified
                      </small>
                      <div className="d-flex align-items-center gap-2 text-muted small">
                        <Clock size={14} className="text-primary" />
                        <span>
                          {defaultData?.updatedAt 
                            ? formatDateTime(defaultData?.updatedAt) 
                            : formatDateTime(defaultData?.createdAt)}
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default PageDetail;